import type { ScannedInstance } from '~/domain/dicom/importScan';
import type { FileTreeLimits } from '~/utils/io/files/fileTree';
import type { ImportMessage } from './importMessage';
import type { ImportOutcome } from './importModel';

/** What a commit is, independent of how it is performed. The reducer speaks this; `useImportCommit` implements it. */

/**
 * What one `/api/dicom/instances` request may carry; mirrors the connector's own upload cap.
 *
 * The framing figure over-estimates the 105 fixed characters plus boundary token that each part
 * costs beyond its filename: the cap here equals the server's exactly, so any under-count is an
 * upload accepted by the wizard and refused by the connector.
 */
export const IMPORT_LIMITS: FileTreeLimits = {
    maxBytes: 512 * 1024 * 1024,
    partFramingBytes: 256,
};

export type CommitPhase =
    | { readonly kind: 'uploading'; readonly totalFiles: number }
    | { readonly kind: 'saving-context' };

/** commit's own resolved result: the definitive outcome of this call the moment it settles. */
export type ImportCommitResult =
    | { readonly ok: true; readonly outcome: ImportOutcome }
    | {
          readonly ok: false;
          readonly message: ImportMessage;
          /** Every record the server confirmed touching; `[]` when nothing landed. */
          readonly landed: readonly ImportOutcome[];
          /** No answer was received, so `landed` does not bound what reached the server. */
          readonly landingUnknown: boolean;
      };

/** The clinical-context fields the wizard collects; narrower than the server's request, so unshown fields are carried forward, not cleared. */
export type ClinicalContext = {
    notes: string | null;
};

export type ImportCommit = (
    instances: readonly ScannedInstance[],
    context: ClinicalContext,
    onPhase: (phase: CommitPhase) => void
) => Promise<ImportCommitResult>;
